import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { Users, Settings, ArrowLeft, Eye, Edit, Trash2, UserCheck, EyeOff, Mail, Activity, ShieldCheck } from 'lucide-react';
import { Badge } from '../ui/badge';
import Logo from '../ui/Logo';
import { useNavigate } from 'react-router-dom';
import EmailSettingsModal from './EmailSettingsModal';
import { hasAdminAccess, isManagerOf } from '../../lib/permissions';

interface TeamMember {
  id: string;
  email: string;
  displayName: string;
  photoURL?: string;
  role: 'employee' | 'manager' | 'admin';
  managers?: Array<{
    id: string;
    name: string;
    email: string;
  }>;
  // Keep legacy fields for backward compatibility
  managerId?: string;
  managerName?: string;
  directReports?: string[];
  createdAt: any;
  lastLogin?: any;
  isActive: boolean;
  upForReview?: boolean;
  reviewCycle?: string;
  upForPulseCheck?: boolean;
  isHR?: boolean;
}

const AdminDashboard: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showEmailSettings, setShowEmailSettings] = useState(false);

  // Full admins (super admin / admin role / HR) manage everyone and see the
  // admin configuration tools. Plain managers only manage their own direct
  // reports and see a trimmed-down view.
  const isFullAdmin = hasAdminAccess(currentUser?.email, userProfile);
  const managerOnly = !isFullAdmin;

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        if (!currentUser) return;

        // Fetch real user data from Firestore
        const { collection, getDocs } = await import('firebase/firestore');
        const { db } = await import('../../lib/firebase');

        const usersRef = collection(db, 'users');
        const snapshot = await getDocs(usersRef);

        const members: TeamMember[] = snapshot.docs.map(doc => {
          const data = doc.data();
          
          // Handle both new multiple managers format and legacy single manager
          let managers: Array<{id: string, name: string, email: string}> = [];
          if (data.managers && Array.isArray(data.managers)) {
            managers = data.managers;
          } else if (data.managerId && data.managerName) {
            // Convert legacy single manager to new format
            const legacyManager = snapshot.docs.find(d => d.id === data.managerId);
            if (legacyManager) {
              managers = [{
                id: data.managerId,
                name: data.managerName,
                email: legacyManager.data().email || ''
              }];
            }
          }
          
          return {
            id: doc.id,
            email: data.email || '',
            displayName: data.displayName || data.email?.split('@')[0] || 'Unknown',
            photoURL: data.photoURL || undefined,
            role: data.role || 'employee',
            managers,
            // Keep legacy fields for backward compatibility
            managerId: data.managerId || undefined,
            managerName: data.managerName || undefined,
            isActive: data.isActive !== false,
            createdAt: data.createdAt?.toDate() || new Date(),
            lastLogin: data.lastLogin?.toDate() || undefined,
            directReports: [],
            upForReview: data.upForReview || false,
            reviewCycle: data.reviewCycle || undefined,
            upForPulseCheck: data.upForPulseCheck || false,
            isHR: data.isHR || false
          };
        });
        
        // Managers only see their own direct reports; admins/HR see everyone.
        const visibleMembers = managerOnly
          ? members.filter(member => isManagerOf(currentUser.uid, member.managers))
          : members;

        // Sort by display name
        visibleMembers.sort((a, b) => a.displayName.localeCompare(b.displayName));

        setTeamMembers(visibleMembers);
        console.log(`Admin dashboard loaded: ${visibleMembers.length} team members found`);
      } catch (err: any) {
        console.error('Error fetching team members:', err);
        setError(err.message || 'Failed to load team members');
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, managerOnly]);

  const handleToggleReviewStatus = async (memberId: string, upForReview: boolean) => {
    try {
      if (!currentUser) return;

      // Update in Firestore
      const { doc, updateDoc, serverTimestamp, Timestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const userRef = doc(db, 'users', memberId);
      const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format for display
      // Use full timestamp so we can properly compare against submission times
      const reviewCycleTimestamp = upForReview ? Timestamp.now() : null;
      
      await updateDoc(userRef, {
        upForReview: upForReview,
        reviewCycleStart: reviewCycleTimestamp, // Full timestamp for accurate comparison
        reviewCycle: upForReview ? currentDate : null, // Keep date string for display
        reviewStatusUpdatedAt: serverTimestamp(),
        reviewStatusUpdatedBy: currentUser.uid
      });
      
      // Update local state
      const updatedMembers = teamMembers.map(member => 
        member.id === memberId 
          ? { 
              ...member, 
              upForReview: upForReview,
              reviewCycle: upForReview ? currentDate : undefined
            }
          : member
      );
      setTeamMembers(updatedMembers);
      setError('');
    } catch (err: any) {
      console.error('Error updating review status:', err);
      setError(err.message || 'Failed to update review status');
    }
  };

  const handleTogglePulseCheckStatus = async (memberId: string, upForPulseCheck: boolean) => {
    try {
      if (!currentUser) return;

      const { doc, updateDoc, serverTimestamp, Timestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const userRef = doc(db, 'users', memberId);
      const pulseCheckCycleTimestamp = upForPulseCheck ? Timestamp.now() : null;
      
      await updateDoc(userRef, {
        upForPulseCheck: upForPulseCheck,
        pulseCheckCycleStart: pulseCheckCycleTimestamp,
        pulseCheckStatusUpdatedAt: serverTimestamp(),
        pulseCheckStatusUpdatedBy: currentUser.uid
      });
      
      // Update local state
      const updatedMembers = teamMembers.map(member => 
        member.id === memberId 
          ? { ...member, upForPulseCheck: upForPulseCheck }
          : member
      );
      setTeamMembers(updatedMembers);
      setError('');
    } catch (err: any) {
      console.error('Error updating pulse check status:', err);
      setError(err.message || 'Failed to update pulse check status');
    }
  };

  const usersUpForReview = teamMembers.filter(member => member.upForReview);

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the team? This action cannot be undone.`)) {
      return;
    }

    try {
      const { doc, deleteDoc } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const memberRef = doc(db, 'users', memberId);
      await deleteDoc(memberRef);
      
      // Update local state
      setTeamMembers(prev => prev.filter(m => m.id !== memberId));
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to remove team member');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </Button>
            <div className="flex items-center gap-4">
              <Logo className="text-primary" width={120} height={60} />
              <div>
                <h1 className="text-2xl font-bold text-primary">
                  {managerOnly ? 'Team Management' : 'Admin Dashboard'}
                </h1>
                <div className="flex items-center gap-2">
                  <p className="text-muted-foreground">{currentUser?.email}</p>
                  <Badge variant={managerOnly ? 'default' : 'destructive'}>
                    {managerOnly ? 'manager' : 'admin'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Email Settings Button (admin-only) */}
          {!managerOnly && (
            <Button
              variant="outline"
              onClick={() => setShowEmailSettings(true)}
              className="flex items-center gap-2"
            >
              <Mail className="h-4 w-4" />
              Email Settings
            </Button>
          )}
        </div>
      </header>

      {/* Email Settings Modal */}
      {!managerOnly && (
        <EmailSettingsModal
          open={showEmailSettings}
          onOpenChange={setShowEmailSettings}
        />
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Quick actions — managers see team views; admins also manage templates */}
        {managerOnly ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">

          <Card
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/reviews')}
          >
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Team Peer Reviews</CardTitle>
              <Eye className="h-4 w-4 ml-auto text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <CardDescription>
                View peer review responses for your team
              </CardDescription>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/team/pulse-responses')}
          >
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Team Pulse Responses</CardTitle>
              <Eye className="h-4 w-4 ml-auto text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <CardDescription>
                View engagement pulse responses for your team
              </CardDescription>
            </CardContent>
          </Card>
        </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">

          <Card
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/questions')}
          >
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Review Questions</CardTitle>
              <Settings className="h-4 w-4 ml-auto text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <CardDescription>
                Create and manage peer review questionnaires
              </CardDescription>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/pulse-questions')}
          >
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Engagement Pulse Questions</CardTitle>
              <Settings className="h-4 w-4 ml-auto text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <CardDescription>
                Create and manage self-reflection surveys
              </CardDescription>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/reviews')}
          >
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Review Peer Responses</CardTitle>
              <Eye className="h-4 w-4 ml-auto text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <CardDescription>
                View and manage peer review responses
              </CardDescription>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/pulse-responses')}
          >
            <CardHeader className="flex flex-row items-center space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Engagement Pulse Responses</CardTitle>
              <Eye className="h-4 w-4 ml-auto text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <CardDescription>
                View engagement pulse survey responses
              </CardDescription>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Peer Review Stats */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Peer Review Status
            </CardTitle>
            <CardDescription>Manage who is currently up for peer review</CardDescription>
          </CardHeader>
          <CardContent>
            {usersUpForReview.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Currently up for review:</p>
                <div className="flex flex-wrap gap-2">
                  {usersUpForReview.map(user => (
                    <span key={user.id} className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                      {user.displayName}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Members List */}
        <Card>
          <CardHeader>
            <CardTitle>{managerOnly ? 'Your Team' : 'Team Members'} ({teamMembers.length})</CardTitle>
            <CardDescription>
              {managerOnly
                ? 'Enable peer review and pulse check for your direct reports'
                : 'Manage team members and review assignments'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {teamMembers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {managerOnly
                  ? 'You have no direct reports assigned yet. Contact an admin to assign your team.'
                  : 'No team members found.'}
              </p>
            ) : (
            <div className="space-y-4">
              {teamMembers.map((member) => (
                <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    {member.photoURL ? (
                      <img src={member.photoURL} alt={member.displayName} className="w-12 h-12 rounded-full" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-6 w-6 text-primary" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{member.displayName}</p>
                        <Badge variant={
                          member.role === 'admin' ? 'destructive' :
                          member.role === 'manager' ? 'default' : 
                          'secondary'
                        }>
                          {member.role}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                      {member.managers && member.managers.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Reports to: {member.managers.map(m => m.name).join(', ')}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {member.upForReview && (
                          <Badge variant="outline" className="text-green-700 border-green-300">
                            <UserCheck className="h-3 w-3 mr-1" />
                            Up for Peer Review
                          </Badge>
                        )}
                        {member.isHR && (
                          <Badge variant="outline" className="text-purple-700 border-purple-300">
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            HR
                          </Badge>
                        )}
                        {member.upForPulseCheck && (
                          <Badge variant="outline" className="text-blue-700 border-blue-300">
                            <Activity className="h-3 w-3 mr-1" />
                            Pulse Check Enabled
                          </Badge>
                        )}
                      </div>
                      {member.upForReview && member.reviewCycle && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Review cycle: {member.reviewCycle}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant={member.upForReview ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleToggleReviewStatus(member.id, !member.upForReview)}
                      className="flex items-center gap-1"
                    >
                      {member.upForReview ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      {member.upForReview ? 'Remove from Peer Review' : 'Add to Peer Review'}
                    </Button>
                    <Button
                      variant={member.upForPulseCheck ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleTogglePulseCheckStatus(member.id, !member.upForPulseCheck)}
                      className="flex items-center gap-1"
                    >
                      <Activity className="h-3 w-3" />
                      {member.upForPulseCheck ? 'Disable Pulse Check' : 'Enable Pulse Check'}
                    </Button>
                    {!managerOnly && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/users?edit=${member.id}`)}
                          className="flex items-center gap-1"
                        >
                          <Edit className="h-3 w-3" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveMember(member.id, member.displayName)}
                          className="flex items-center gap-1 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" />
                          Remove
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
